import client, { clientTransformed } from '@common/utils/rest';
import { executeUI } from '@common/utils/ui-bridge';
import { getQueriesAsSearch } from '@common/utils';
import { ROOT_SYSTEM_FIELDS_ID } from '../constants/folders';
import { INDIVIDUALS_MODULE_ID } from '../constants/module-id';

const URL_PREFIX = 'api/individuals';
const INDIVIDUALS_ID = 1;

const getUrl = (path) => {
  return `${URL_PREFIX}/${path}`;
};

export const getIndividualsColumns = () => {
	return clientTransformed({
    path: getUrl('columns'),
    method: 'GET'
  }).then(
    (response) => {
      return response.entity;
    },
  );
};

export const saveIndividualsColumns = (columns) => {
	return clientTransformed({
    path: getUrl('columns'),
    method: 'POST',
		entity: columns,
  });
};

export const showProbandsOnly = () => {
  return clientTransformed({
    path: getUrl('showProbandsOnly'),
    method: 'GET'
  }).then(response => response.entity);
};

export const getIndividuals = ({ filtering, pagination }) => {
	return clientTransformed({
    path: getUrl('smartlist'),
    method: 'POST',
		entity: {
			...filtering,
			...pagination,
		},
  }).then(
    (response) => {
      return response.entity;
    },
  );
};

export const getProfiles = () => {
  return client({
    path: 'api/userMaintenance/profiles',
    method: 'GET'
  }).then(
    (response) => {
      return response.entity;
    },
  );
};

export const checkIndividualNameExists = ({ homeFolderId, name }) => {
  return clientTransformed({
    path: getUrl('checkIndividualNameExists'),
    params: {
      folderId: homeFolderId,
      individualName: name
    },
    method: 'GET',
  }).then(response => response.entity);
};

export const createIndividual = (entity) => {
  return client({
    path: getUrl('createIndividual'),
    entity,
    method: 'POST',
  }).then(response => response.entity);
};

export const getFolders = () => {
  return client({
    path: 'api/folders/module/loadFolders',
    params: {
      folderTypeId: INDIVIDUALS_ID
    },
    method: 'GET',
  }).then((response) => {
    const createRootFolder = ({ id, name, children, imageURL, system }) => {
      return {
        id, 
        name,
        children,
        imageURL,
        system,
        imageURLSelected: imageURL,
      };
    };
    
    const foldersByType = response.entity.reduce((acc, folder) => {
      if (folder.system) {
        folder.imageURL = 'gif/system.gif';
        folder.imageURLSelected = 'gif/system.gif';

        acc.system.push(folder);
      } else {
        acc.custom.push(folder);
      }

      return acc;
    }, { custom: [], system: [] });

    const systemRootFolder = createRootFolder({
      id: ROOT_SYSTEM_FIELDS_ID,
      name: 'System Fields',
      children: foldersByType.system,
      imageURL: 'gif/system.gif',
      system: true,
    });
    const customRootFolder = createRootFolder({
      id: '',
      name: 'Individual Data Folders',
      children: foldersByType.custom,
      imageURL: 'gif/df_individual.gif',
      system: false,
    });

    return [
      systemRootFolder,
      customRootFolder
    ];
  });
};

export const getFields = ({ searchQuery, folderId, system, contextId }) => {
  return clientTransformed({
    path: 'api/fieldsResource/load/fields',
    params: {
      searchQuery,
      parentId: folderId,
      contextId,
      system,
    },
    method: 'GET',
  }).then((response) => {
    return response.entity;
  });
};

export const applyCurrentColumnsToAllUsers = (columns) => {
	return clientTransformed({
    path: 'api/smartListAdmin/applyCurrentColumnsToAllUsers',
    method: 'POST',
		entity: {
      columns,
      moduleId: INDIVIDUALS_MODULE_ID
    },
  });
};

export const applyCurrentColumnsToSpecificProfiles = (columns, profilesFormat) => {
	return clientTransformed({
    path: 'api/smartListAdmin/applyCurrentColumnsToSpecificProfiles',
    method: 'POST',
		entity: {
      columns,
      profilesFormat,
      moduleId: INDIVIDUALS_MODULE_ID
    },
  });
};

export const setDefaultColumnsForToAllUsers = (columns) => {
	return clientTransformed({
    path: 'api/smartListAdmin/setDefaultForAllUsers',
    method: 'POST',
		entity: {
      columns,
      moduleId: INDIVIDUALS_MODULE_ID
    },
  });
};

export const setDefaultColumnsForSpecificUsers = (columns, profilesFormat) => {
	return clientTransformed({
    path: 'api/smartListAdmin/setDefaultForSpecificProfiles ',
    method: 'POST',
		entity: {
      columns,
      profilesFormat,
      moduleId: INDIVIDUALS_MODULE_ID
    },
  });
};

export const restoreColumnsToDefault = (columns) => {
	return clientTransformed({
    path: 'api/smartListAdmin/restoreColumnsToDefault',
    method: 'POST',
		entity: {
      columns,
      moduleId: INDIVIDUALS_MODULE_ID
    },
  }).then((response) => {
    return  response.entity;
  });
};

export const getAllowedFeatures = () => {
  return clientTransformed({
    path: 'api/smartListAdmin/adminFunctionsList',
    method: 'GET',
  }).then((response) => {
    return  response.entity;
  });
};

export const renameIndividual = (globalId, individualName) => {
	return clientTransformed({
    path: 'api/individuals/renameIndividual',
    method: 'POST',
		params: {
      globalId,
      individualName,
    },
  });
};

const moduleName = 'INDIVIDUALS';

export const openDatasheet = (globalId) => {
  executeUI(moduleName, 'openDatasheet', globalId, '');
};

export const openPedigree = (globalId) => {
  executeUI(moduleName, 'openPedigree', globalId, '');
};

export const openTestResults = (globalId) => {
  executeUI(moduleName, 'openTestResults', globalId, '');
};

export const generateLetter = (globalId) => {
  executeUI(moduleName, 'generateLetter', globalId, '');
};

export const sendInvite = (invite) => {
	return clientTransformed({
    path: getUrl('sendInvite'),
    method: 'POST',
		entity: invite,
  });
};

export const getIndividualEmail = ({ wdcfolderId, globalId }) => {
	return clientTransformed({
    path: getUrl('getIndividualEmail'),
    method: 'GET',
		params: {
      wdcfolderId,
      globalId: globalId,
    }
  }).then((response) => {
    return response.entity;
  });
};

export const saveEmailForIndividual = (data) => {
	return clientTransformed({
    path: getUrl('saveEmailForIndividual'),
    method: 'POST',
		entity: data
  });
};

export const getIndividualFamilyData = (globalId) => {
	return clientTransformed({
    path: getUrl('getIndividualFamilyId'),
    method: 'GET',
		params: {
      globalId
    }
  }).then((response) => {
    return response.entity;
  });
};


export const getFamilyIdMembersCount = (familyId) => {
	return clientTransformed({
    path: getUrl('getFamilyIdMemberCount'),
    method: 'GET',
		params: {
      familyId
    }
  }).then((response) => {
    return response.entity;
  });
};

export const getPredefinedQuestionnaires = async (globalId) => {
  return clientTransformed({
    path: 'api/individuals/openQuestionnaire',
    params: {
      globalId,
    },
    method: 'GET'
  }).then(
    (response) => {
      return response.entity;
    },
  );
};

export const launchQuestionnaire = (inviteId, baseUrl, fhq4) => {
  return client({
    params: {
      inviteId,
      baseUrl,
      fhq4,
    },
    path: 'api/individuals/launchQuestionnaire',
    method: 'GET',
  }).then((response) => {
    return response.entity;
  });
};

export const validateInvitesEmail = (wdcFolderId, invitesGlobalIds) => {
	return client({
    path: `${getUrl('validateInviteIndividualsEmails')}${getQueriesAsSearch({ wdcFolderId, globalIds: invitesGlobalIds })}`,
    method: 'GET',
  }).then((response) => {
    return response.entity;
  });
};